import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormulationEntry } from "@/utils/types/compound";
import type { Variant } from "@/utils/types/variant";

type VariantMutationCallbacks = {
  // Called after the user forks. Should kick off a server create. The hook
  // does its own optimistic insert before this fires; the callback is purely
  // for persistence side-effects.
  onFork?: (newVariant: Variant, sourceVariant: Variant) => void;
  onRename?: (variant: Variant, name: string) => void;
  onRemove?: (variant: Variant) => void;
};

type UseFormulationVariantsArgs = {
  initialFormulation: FormulationEntry[];
  // Optional server-seeded variants. When provided, the local v1/v2/v3 demo
  // seed is skipped — the server is the source of truth.
  initialVariants?: Variant[] | null;
  // The default-active variant id. Falls back to the first variant.
  initialActiveVariantId?: string | null;
  callbacks?: VariantMutationCallbacks;
};

type UseFormulationVariantsResult = {
  variants: Variant[];
  activeVariantId: string;
  activeVariant: Variant;
  /**
   * True once server-side variants have been applied (or when the caller
   * isn't waiting for server data at all). Callers use this to keep the
   * skeleton up until real data renders — without it, the WorkbenchPage
   * would briefly paint the placeholder variant after the skeleton drops
   * and before the seed effect commits.
   */
  isSeeded: boolean;
  setActiveVariantId: (id: string) => void;
  setFormulation: (
    next:
      | FormulationEntry[]
      | ((prev: FormulationEntry[]) => FormulationEntry[])
  ) => void;
  patchVariant: (id: string, patch: Partial<Variant>) => void;
  forkVariant: (prompt?: string | null) => string;
  renameVariant: (id: string, name: string) => void;
  removeVariant: (id: string) => void;
};

// Stable placeholder returned for `activeVariant` while no real variants
// exist yet. Keeping `activeVariant: Variant` (vs. `Variant | undefined`)
// lets every downstream consumer keep its `.name` / `.formulaId` reads
// without optional-chaining churn — the caller gates rendering with
// `isSeeded` so this placeholder is never user-visible.
const PLACEHOLDER_VARIANT: Variant = {
  id: "__pending__",
  name: "",
  formulation: [],
  fixedIngredients: [],
  prompt: null,
  createdAt: 0,
};

export function useFormulationVariants({
  // `initialFormulation` is no longer used to fabricate demo variants — it
  // exists only for backwards compatibility with the prop shape; remove
  // when no caller passes it.
  initialFormulation: _initialFormulation,
  initialVariants,
  initialActiveVariantId,
  callbacks,
}: UseFormulationVariantsArgs): UseFormulationVariantsResult {
  // NOTE: useState's initial value is captured once on mount. The
  // `seededFromServerRef` below works in concert with this — both rely on
  // WorkbenchPage remounting (via route change) when the user navigates to
  // a different flavor. If this hook is ever used without that remount
  // boundary, both the initial state and the ref guard would diverge.
  //
  // No mock demo seed: when initialVariants is null on mount we start with
  // an empty variants list and a placeholder activeVariant. The page's
  // skeleton stays up (gated on `isSeeded`) until the server response lands.
  const [variants, setVariants] = useState<Variant[]>(initialVariants ?? []);
  // Resync when the server returns new variants. Server data wins on conflict.
  const seededFromServerRef = useRef(false);
  // Render-visible mirror of seededFromServerRef so consumers can re-evaluate.
  const [isSeeded, setIsSeeded] = useState<boolean>(() => Boolean(initialVariants));
  useEffect(() => {
    if (initialVariants && !seededFromServerRef.current) {
      setVariants(initialVariants);
      seededFromServerRef.current = true;
      setIsSeeded(true);
    }
  }, [initialVariants]);

  const [activeVariantId, setActiveVariantId] = useState<string>(
    initialActiveVariantId ?? initialVariants?.[0]?.id ?? PLACEHOLDER_VARIANT.id,
  );

  // Keep activeVariantId valid when the variants list changes underfoot.
  useEffect(() => {
    if (variants.length === 0) return;
    if (!variants.find((v) => v.id === activeVariantId)) {
      const next = variants[0];
      if (next) setActiveVariantId(next.id);
    }
  }, [variants, activeVariantId]);

  const activeVariant = useMemo(
    () =>
      variants.find((v) => v.id === activeVariantId) ||
      variants[0] ||
      PLACEHOLDER_VARIANT,
    [variants, activeVariantId],
  );

  const setFormulation = useCallback<UseFormulationVariantsResult["setFormulation"]>(
    (next) => {
      setVariants((vs) =>
        vs.map((v) =>
          v.id === activeVariantId
            ? {
                ...v,
                formulation:
                  typeof next === "function" ? next(v.formulation) : next,
              }
            : v,
        ),
      );
    },
    [activeVariantId],
  );

  const patchVariant = useCallback(
    (id: string, patch: Partial<Variant>) => {
      setVariants((vs) =>
        vs.map((v) => (v.id === id ? { ...v, ...patch } : v)),
      );
    },
    [],
  );

  const forkVariant = useCallback<
    UseFormulationVariantsResult["forkVariant"]
  >(
    (prompt = null) => {
      // Strip both `v` (local-fork) and `f` (server-formula_number) prefixes
      // so the next-N counter doesn't collide with server-seeded variants.
      const maxN = variants.reduce((m, v) => {
        const n = parseInt(String(v.id).replace(/^[fv]/, ""), 10);
        return Number.isFinite(n) && n > m ? n : m;
      }, 0);
      const nextN = maxN + 1;
      // Local id uses the same "v<N>" / "f<N>" prefix existing UI expects.
      // Server-backed variants use "f<formulaNumber>" via dtoToVariant.
      const id = `v${nextN}`;
      const sourceFormulation = activeVariant.formulation.map((f) => ({ ...f }));
      const sourceFixed = (activeVariant.fixedIngredients ?? []).map((f) => ({ ...f }));
      const newVar: Variant = {
        id,
        name: `v${nextN}`,
        formulation: sourceFormulation,
        fixedIngredients: sourceFixed,
        prompt,
        createdAt: Date.now(),
        formulaId: null,
        formulaNumber: null,
        currentVersionId: null,
      };
      // Append (not prepend) so the local order matches the server's
      // canonical ORDER BY sort_order ASC. The server's POST /formulas
      // allocates `sort_order = maxSort + 1` for new forks, which lands
      // them at the END of the list on refresh — so prepending here would
      // cause the tab to jump from leftmost to rightmost after refresh.
      setVariants((vs) => [...vs, newVar]);
      setActiveVariantId(id);
      callbacks?.onFork?.(newVar, activeVariant);
      return id;
    },
    [variants, activeVariant, callbacks],
  );

  const renameVariant = useCallback(
    (id: string, name: string) => {
      let target: Variant | undefined;
      setVariants((vs) => {
        const next = vs.map((v) => (v.id === id ? { ...v, name } : v));
        target = next.find((v) => v.id === id);
        return next;
      });
      if (target) callbacks?.onRename?.(target, name);
    },
    [callbacks],
  );

  const removeVariant = useCallback(
    (id: string) => {
      // Compute the next active id inside the updater (mutation-only —
      // idempotent under StrictMode's double-run), then call setActiveVariantId
      // and onRemove AFTER. Side effects from inside a functional updater
      // would fire twice in dev StrictMode and are at-risk under concurrent
      // rendering features.
      let removed: Variant | undefined;
      let nextActiveId: string | undefined;
      setVariants((vs) => {
        const r = vs.find((v) => v.id === id);
        const next = vs.filter((v) => v.id !== id);
        if (next.length === 0) return vs;       // refuse to empty the list
        removed = r;
        if (id === activeVariantId) nextActiveId = next[0]!.id;
        return next;
      });
      if (nextActiveId) setActiveVariantId(nextActiveId);
      if (removed) callbacks?.onRemove?.(removed);
    },
    [activeVariantId, callbacks],
  );

  return {
    variants,
    activeVariantId,
    activeVariant,
    isSeeded,
    setActiveVariantId,
    setFormulation,
    patchVariant,
    forkVariant,
    renameVariant,
    removeVariant,
  };
}
